import React, { useEffect, useState, useMemo } from 'react';
import { Button } from "components/ui/button";
import { Link } from "react-router-dom";
import { Upload, Plus, CheckCircle2, AlertTriangle, X } from 'lucide-react'; // Added icons
import { useCrew } from '../context/CrewContext';
import PageTitle from '../components/generic/PageTitle';
import CrewCard from 'components/crewManagement/CrewCard';
import Breadcrumbs from 'components/generic/Breadcrumbs';
import Tabs from 'components/generic/FilterTabs';
import CrewDetailsModal from 'components/crewManagement/CrewDetailsModal';
import BulkUploadCrew from './BulkUploadCrew';
import { toast } from 'react-hot-toast'; // Import toast

const CrewManagementPage = () => {
  const { crewMembers, loading, error, fetchAllCrew, publishCrew } = useCrew();

  const [selectedTab, setSelectedTab] = useState("All Crew Members");
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isBulkUploading, setIsBulkUploading] = useState(false);

  const [page, setPage] = useState(1);
  const limit = 50;
  const [hasMore, setHasMore] = useState(true);

  // --- Toast Helper Functions ---
  const showSuccessToast = (title, message) => {
    toast.custom(
      (t) => (
        <div className={`${t.visible ? "animate-enter" : "animate-leave"} max-w-md w-full bg-[#F0FDF4] border border-[#22C55E] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}>
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-[#22C55E]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button onClick={() => toast.dismiss(t.id)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  const showErrorToast = (title, message) => {
    toast.custom(
      (t) => (
        <div className={`${t.visible ? "animate-enter" : "animate-leave"} max-w-md w-full bg-[#FEF2F2] border border-[#EF4444] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}>
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button onClick={() => toast.dismiss(t.id)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  // --- Logic to handle approval ---
  const handleApproveMember = async (crewMember) => {
    try {
      const result = await publishCrew(crewMember.crew_member_id);
      // Assuming publishCrew returns the updated member or a truthy value on success
      if (result) {
        showSuccessToast(
          "Member Approved",
          `${crewMember.first_name} ${crewMember.last_name} has been published successfully.`
        );
      }
    } catch (err) {
      showErrorToast("Approval Failed", err.message || "An error occurred while approving the member.");
    }
  };

  const availableTabs = useMemo(() => {
    const userString = localStorage.getItem('user');
    let organizationType = null;

    try {
      if (userString) {
        const userData = JSON.parse(userString);
        organizationType = userData.organizationType;
      }
    } catch (error) {
      console.error("Failed to parse user data from localStorage", error);
    }

    if (organizationType === 2) {
      return ["All Crew Members", "Draft"];
    }

    return ["All Crew Members", "Beige Crew", "Memehouse Crew", "Draft"];
  }, []);

  useEffect(() => {
    fetchAllCrew({ page: 1, limit, append: false }).then((data) => {
      setHasMore(data?.length === limit);
    });
  }, []);

  const loadMore = () => {
    const nextPage = page + 1;
    fetchAllCrew({ page: nextPage, limit, append: true }).then((data) => {
      if (!data || data.length < limit) {
        setHasMore(false);
      }
    });
    setPage(nextPage);
  };

  const filteredMembers = useMemo(() => {
    return crewMembers.filter((cm) => {
      if (selectedTab === "All Crew Members") return true;
      if (selectedTab === "Beige Crew") return cm.is_beige_member === 1;
      if (selectedTab === "Memehouse Crew") return cm.is_beige_member === 0;
      if (selectedTab === "Draft") return cm.is_draft === true;
      return true;
    });
  }, [crewMembers, selectedTab]);

  const openCrewModal = (crew) => {
    setSelectedCrew(crew);
    setShowModal(true);
  };

  if (isBulkUploading) {
    return (
      <BulkUploadCrew
        onBack={() => setIsBulkUploading(false)}
        onComplete={() => {
          setIsBulkUploading(false);
          fetchAllCrew({ page: 1, limit: 50, append: false });
        }}
      />
    );
  }

  return (
    <div className="crew-mngt-content p-6">
      <Breadcrumbs />
      <div className="flex flex-wrap justify-between items-start mb-2">
        <PageTitle
          title="Crew Management"
          description="Manage your team and track task progress for active projects."
        />
        <div className="flex gap-2 mb-2 md:mb-0">
          <Button
            variant="outline"
            onClick={() => setIsBulkUploading(true)}
            className="rounded-lg py-2 flex gap-2"
          >
            <Upload className="w-4 h-4" />
            Bulk Upload
          </Button>

          <Button asChild className="bg-button-coloured rounded-lg py-2">
            <Link to="/crew-management/add-member" className="flex justify-center items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Crew Member
            </Link>
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <CrewDetailsModal
        crewMember={selectedCrew}
        open={showModal}
        onOpenChange={setShowModal}
      />

      <Tabs
        tabs={availableTabs}
        value={selectedTab}
        onChange={setSelectedTab}
      />

      {loading && crewMembers.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <p className="text-muted-foreground">Loading crew members...</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredMembers.map((crewMember) => (
              <div key={crewMember.crew_member_id} className="w-full relative">
                <CrewCard
                  crewMember={crewMember}
                  onViewProfile={() => openCrewModal(crewMember)}
                  // Pass the new handler here
                  onApprove={() => handleApproveMember(crewMember)}
                />
              </div>
            ))}
          </div>

          {filteredMembers.length === 0 && !loading && (
            <div className="flex flex-col justify-center items-center py-12 text-center">
              <p className="text-muted-foreground mb-1">
                No {selectedTab.toLowerCase()} available
              </p>
              {hasMore && (
                <p className="text-xs text-muted-foreground">
                  Try clicking "View More" to load more data.
                </p>
              )}
            </div>
          )}

          {hasMore && (
            <div className="flex flex-col items-center justify-center mt-8 gap-2">
              <Button
                className="bg-button-coloured px-6 py-2"
                onClick={loadMore}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'View More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CrewManagementPage;
